import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

/**
 * Convalesce-style Invoice Builder (JS)
 * - Matches MyShifts white-card layout
 * - Uses exact minutes from checkInTime/checkOutTime or planned shift time
 * - Supports adding custom expenses / materials
 * - Saves final invoice into Firestore: invoices collection
 */

function toDateSafe(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === "object" && typeof v.toDate === "function") return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function minutesBetween(start, end) {
  const s = toDateSafe(start);
  const e = toDateSafe(end);
  if (!s || !e) return 0;
  const diff = e.getTime() - s.getTime();
  return diff > 0 ? Math.floor(diff / 60000) : 0; // exact minutes (no rounding up)
}

function plannedMinutesFromShift(s) {
  const startStr = s.startTime || s.start || s.plannedStart;
  const endStr = s.endTime || s.end || s.plannedEnd;

  const anchor = toDateSafe(s.date || s.shiftDate || s.checkInTime || s.checkOutTime);
  if (!anchor || !startStr || !endStr) return 0;

  const [sh, sm] = String(startStr).split(":").map(Number);
  const [eh, em] = String(endStr).split(":").map(Number);

  if (![sh, sm, eh, em].every((n) => Number.isFinite(n))) return 0;

  const start = new Date(anchor);
  start.setHours(sh, sm, 0, 0);

  const end = new Date(anchor);
  end.setHours(eh, em, 0, 0);

  const diff = end.getTime() - start.getTime();
  return diff > 0 ? Math.floor(diff / 60000) : 0;
}

function formatMoneyGBP(n) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatHM(d) {
  const dt = toDateSafe(d);
  if (!dt) return "—";
  return `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}

function formatDateLong(d) {
  const dt = toDateSafe(d);
  if (!dt) return "Date unknown";
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function inRange(date, startStr, endStr) {
  const d = toDateSafe(date);
  if (!d) return false;
  const start = startStr ? new Date(startStr + "T00:00:00") : null;
  const end = endStr ? new Date(endStr + "T23:59:59") : null;
  if (start && d < start) return false;
  if (end && d > end) return false;
  return true;
}

export default function InvoiceManager() {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);

  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Admin form fields (editable)
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [submittedOn, setSubmittedOn] = useState(() =>
    new Date().toLocaleDateString("en-GB")
  );
  const [dueDate, setDueDate] = useState("");
  const [serviceType, setServiceType] = useState("iADL support");
  const [invoiceFor, setInvoiceFor] = useState("");
  const [payableTo, setPayableTo] = useState("Convalesce Care and Recruitment Limited");

  // Bank/notes (editable)
  const [bankLine1, setBankLine1] = useState("Monzo: Convalesce care and recruitment Limited");
  const [bankLine2, setBankLine2] = useState("Sort code: 04-00-06");
  const [bankLine3, setBankLine3] = useState("Acct: 29285531");

  // VAT handling
  const [vatApplicable, setVatApplicable] = useState(false);
  const [vatPercent, setVatPercent] = useState(0);

  // Rate default
  const [defaultRate, setDefaultRate] = useState(16);
  const [usePlannedTime, setUsePlannedTime] = useState(false);

  const [clientEmail, setClientEmail] = useState("");

  // Shifts loaded
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);

  // Custom Line Items (Expenses / Materials)
  const [customItems, setCustomItems] = useState([]);
  const [customDesc, setCustomDesc] = useState("");
  const [customAmount, setCustomAmount] = useState("");

  const [saveState, setSaveState] = useState({ saving: false, ok: "", err: "" });

  const [savedInvoices, setSavedInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState("");
  const [loadedInvoice, setLoadedInvoice] = useState(null);

  // Load clients
  useEffect(() => {
    (async () => {
      try {
        setLoadingClients(true);
        const snap = await getDocs(collection(db, "clients"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => String(a.name || a.clientName || "").localeCompare(String(b.name || b.clientName || "")));
        setClients(list);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingClients(false);
      }
    })();
  }, []);

  // Load saved invoices
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "invoices"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => String(a.invoiceNumber || "").localeCompare(String(b.invoiceNumber || "")));
        setSavedInvoices(list);
      } catch (e) {
        console.error("Failed to load invoices list", e);
      }
    })();
  }, []);

  useEffect(() => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) return;
    setClientEmail(c.email || c.clientEmail || "");
  }, [clientId, clients]);

  useEffect(() => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) {
      setClientName("");
      return;
    }
    const name = c.name || c.clientName || c.fullName || "";
    setClientName(name);
    setInvoiceFor(name);
  }, [clientId, clients]);

  const fetchShifts = async () => {
    if (!clientName) return;
    setLoadingShifts(true);
    setShifts([]);

    try {
      const q1 = query(
        collection(db, "shifts"),
        where("clientName", "==", clientName),
        where("status", "==", "Checked Out")
      );

      const snap = await getDocs(q1);
      const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const filtered = raw.filter((s) => {
        const anchor = s.checkInTime || s.checkOutTime || s.date;
        return inRange(anchor, fromDate, toDate);
      });

      filtered.sort((a, b) => {
        const da = toDateSafe(a.checkInTime || a.date);
        const dbb = toDateSafe(b.checkInTime || b.date);
        return (da?.getTime() || 0) - (dbb?.getTime() || 0);
      });

      setShifts(filtered);
    } catch (e) {
      console.error(e);
      alert("Failed to load shifts. Check console.");
    } finally {
      setLoadingShifts(false);
    }
  };

  // Build shift rows
  const shiftRows = useMemo(() => {
    return shifts.map((s) => {
      const mins = usePlannedTime
        ? plannedMinutesFromShift(s)
        : minutesBetween(s.checkInTime, s.checkOutTime);

      const hours = mins / 60;
      const rate = Number(defaultRate || s.rate || s.hourlyRate || 0);
      const total = hours * rate;

      const anchorD = toDateSafe(s.checkInTime || s.checkOutTime || s.date);

      let inD = null;
      let outD = null;

      if (usePlannedTime) {
        const startStr = s.startTime || s.start || s.plannedStart;
        const endStr = s.endTime || s.end || s.plannedEnd;

        if (anchorD && startStr && endStr) {
          const [sh, sm] = String(startStr).split(":").map(Number);
          const [eh, em] = String(endStr).split(":").map(Number);

          if ([sh, sm, eh, em].every((n) => Number.isFinite(n))) {
            inD = new Date(anchorD);
            inD.setHours(sh, sm, 0, 0);

            outD = new Date(anchorD);
            outD.setHours(eh, em, 0, 0);
          }
        }
      } else {
        inD = toDateSafe(s.checkInTime);
        outD = toDateSafe(s.checkOutTime);
      }

      return {
        shiftId: s.id,
        dateLabel: formatDateLong(anchorD || s.date),
        timeLabel: `(${formatHM(inD)}am-${formatHM(outD)}pm)`,
        qtyHoursDisplay: hours.toFixed(2),
        minutes: mins,
        rate,
        total,
        isCustom: false,
      };
    });
  }, [shifts, defaultRate, usePlannedTime]);

  // Handlers for Custom Items
  const handleAddCustomItem = () => {
    const amt = parseFloat(customAmount);
    if (!customDesc.trim() || isNaN(amt) || amt <= 0) {
      alert("Please enter a valid description and positive amount.");
      return;
    }

    const newItem = {
      shiftId: `custom-${Date.now()}`,
      dateLabel: customDesc,
      timeLabel: "Expense / Material",
      qtyHoursDisplay: "1.00",
      minutes: 0,
      rate: amt,
      total: amt,
      isCustom: true,
    };

    setCustomItems((prev) => [...prev, newItem]);
    setCustomDesc("");
    setCustomAmount("");
  };

  const handleRemoveCustomItem = (id) => {
    setCustomItems((prev) => prev.filter((item) => item.shiftId !== id));
  };

  // Combine shift rows + custom items
  const invoiceRows = useMemo(() => {
    return [...shiftRows, ...customItems];
  }, [shiftRows, customItems]);

  const subtotal = useMemo(() => {
    return invoiceRows.reduce((sum, r) => sum + r.total, 0);
  }, [invoiceRows]);

  const vatAmount = useMemo(() => {
    if (!vatApplicable) return 0;
    const p = Number(vatPercent || 0) / 100;
    return subtotal * p;
  }, [vatApplicable, vatPercent, subtotal]);

  const grandTotal = subtotal + vatAmount;

  const canSave = clientName && invoiceRows.length > 0 && invoiceNumber;

  const saveInvoice = async () => {
    if (!canSave) {
      alert("Please select a client, load shifts or add expense items, and enter an Invoice #.");
      return;
    }

    setSaveState({ saving: true, ok: "", err: "" });

    try {
      const payload = {
        clientId: clientId || null,
        clientName,
        clientEmail: clientEmail || null,
        invoiceFor,
        payableTo,

        invoiceNumber,
        submittedOn,
        dueDate,
        serviceType,

        period: { fromDate: fromDate || null, toDate: toDate || null },

        vatApplicable: !!vatApplicable,
        vatPercent: Number(vatPercent || 0),
        vatAmount,

        subtotal,
        total: grandTotal,

        notes: [bankLine1, bankLine2, bankLine3].filter(Boolean),

        lineItems: invoiceRows.map((r) => ({
          shiftId: r.shiftId,
          description: r.isCustom ? r.dateLabel : `${r.dateLabel} - ${r.timeLabel}`,
          minutes: r.minutes,
          hours: Number(r.qtyHoursDisplay),
          rate: r.rate,
          total: r.total,
          isCustom: !!r.isCustom,
        })),

        createdAt: serverTimestamp(),
        status: "Draft",
      };

      const ref = await addDoc(collection(db, "invoices"), payload);

      setSaveState({
        saving: false,
        ok: `Saved invoice ${invoiceNumber} (doc: ${ref.id})`,
        err: "",
      });
    } catch (e) {
      console.error(e);
      setSaveState({ saving: false, ok: "", err: "Failed to save invoice. Check console." });
    }
  };

  const pageWrap = { paddingTop: "20px" };
  const pageClass = "p-6 pt-20 min-h-screen bg-gray-700/80 text-gray-100";

  const card = {
    maxWidth: "900px",
    margin: "30px auto",
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "24px",
    color: "#1f2937",
    boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
  };

  const innerCard = {
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    padding: "16px",
    backgroundColor: "#f9fafb",
    marginBottom: "8px",
  };

  const label = { fontSize: "12px", color: "#6b7280", fontWeight: 600, marginBottom: 6 };
  const input = {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    padding: "10px 12px",
    fontSize: "13px",
    outline: "none",
  };

  const btn = (bg) => ({
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    backgroundColor: bg,
    color: "#fff",
    fontWeight: "600",
    border: "none",
    cursor: "pointer",
  });

  return (
    <div style={pageWrap} className={pageClass}>
      <h1
        className="no-print"
        style={{
          textAlign: "center",
          color: "gray",
          backgroundColor: "white",
          boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
          width: "fit-content",
          margin: "10px auto",
          fontSize: "16px",
          fontWeight: "500",
          padding: "14px 24px",
          borderRadius: "20px",
        }}
      >
        Invoices
      </h1>

      {/* Builder */}
      <div style={card} className="no-print" id="invoice-builder-wrap">
        <h2 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px" }}>
          Build a weekly / monthly invoice
        </h2>

        <div className="no-print" style={{ display: "grid", gap: 12 }}>
          <div className="no-print" style={innerCard}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={label}>Client</div>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  style={input}
                  disabled={loadingClients}
                >
                  <option value="">{loadingClients ? "Loading clients..." : "Select client"}</option>
                  {clients.map((c) => {
                    const name = c.name || c.clientName || c.fullName || c.id;
                    return (
                      <option key={c.id} value={c.id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <div style={label}>Default rate (£/hr)</div>
                <input
                  style={input}
                  type="number"
                  value={defaultRate}
                  onChange={(e) => setDefaultRate(Number(e.target.value || 0))}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              <div>
                <div style={label}>From</div>
                <input style={input} type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div>
                <div style={label}>To</div>
                <input style={input} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
            </div>

            <button
              onClick={fetchShifts}
              disabled={!clientName || loadingShifts}
              style={{ ...btn("#2563eb"), marginTop: 12 }}
            >
              {loadingShifts ? "Loading shifts..." : "Load completed shifts (Checked Out)"}
            </button>

            <div style={{ marginTop: 10 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={usePlannedTime}
                  onChange={(e) => setUsePlannedTime(e.target.checked)}
                />
                <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>
                  Use assigned shift time (planned start/end) instead of check-in/out
                </div>
              </div>

              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: 6 }}>
                {usePlannedTime ? (
                  <>Hours are calculated from the assigned shift start/end times.</>
                ) : (
                  <>Uses <b>exact minutes</b> from check-in/out timestamps. If a shift has no check-out, it won’t appear here.</>
                )}
              </div>
            </div>
          </div>

          {/* Additional Expenses Section */}
          <div className="no-print" style={innerCard}>
            <div style={label}>Add Additional Expense / Material</div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
              <input
                style={input}
                placeholder="e.g. Cleaning materials purchase"
                value={customDesc}
                onChange={(e) => setCustomDesc(e.target.value)}
              />
              <input
                style={input}
                type="number"
                step="0.01"
                placeholder="Amount (£)"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
              />
              <button
                type="button"
                onClick={handleAddCustomItem}
                style={{
                  padding: "10px",
                  borderRadius: "10px",
                  backgroundColor: "#059669",
                  color: "#fff",
                  fontWeight: "600",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                + Add Item
              </button>
            </div>

            {customItems.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ ...label, marginBottom: 4 }}>Added Custom Items:</div>
                {customItems.map((item) => (
                  <div
                    key={item.shiftId}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: 13,
                      padding: "6px 0",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <span>{item.dateLabel} - <b>{formatMoneyGBP(item.total)}</b></span>
                    <button
                      type="button"
                      onClick={() => handleRemoveCustomItem(item.shiftId)}
                      style={{
                        color: "#dc2626",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: "600",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="no-print" style={innerCard}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={label}>Invoice # (required)</div>
                <input style={input} value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
              </div>

              <div>
                <div style={label}>Submitted on</div>
                <input style={input} value={submittedOn} onChange={(e) => setSubmittedOn(e.target.value)} />
              </div>

              <div>
                <div style={label}>Due date</div>
                <input style={input} value={dueDate} onChange={(e) => setDueDate(e.target.value)} placeholder="e.g. 4/1/26" />
              </div>

              <div>
                <div style={label}>Service type</div>
                <input style={input} value={serviceType} onChange={(e) => setServiceType(e.target.value)} />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <div style={label}>Invoice for</div>
                <input style={input} value={invoiceFor} onChange={(e) => setInvoiceFor(e.target.value)} />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <div style={label}>Client email</div>
                <input style={input} value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <div style={label}>Payable to</div>
                <input style={input} value={payableTo} onChange={(e) => setPayableTo(e.target.value)} />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <div style={label}>Notes (bank details)</div>
                <input style={{ ...input, marginBottom: 8 }} value={bankLine1} onChange={(e) => setBankLine1(e.target.value)} />
                <input style={{ ...input, marginBottom: 8 }} value={bankLine2} onChange={(e) => setBankLine2(e.target.value)} />
                <input style={input} value={bankLine3} onChange={(e) => setBankLine3(e.target.value)} />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={vatApplicable}
                    onChange={(e) => setVatApplicable(e.target.checked)}
                  />
                  <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>
                    VAT applicable
                  </div>
                  {vatApplicable && (
                    <input
                      style={{ ...input, width: 110 }}
                      type="number"
                      value={vatPercent}
                      onChange={(e) => setVatPercent(Number(e.target.value || 0))}
                      placeholder="%"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div style={card}>
        <h2 className="no-print" style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px" }}>
          Invoice preview
        </h2>

        <div className="no-print" style={innerCard}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
            <div>
              <div style={label}>View saved invoice (by Invoice #)</div>
              <select
                style={input}
                value={selectedInvoiceId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedInvoiceId(id);
                  const inv = savedInvoices.find((x) => x.id === id) || null;
                  setLoadedInvoice(inv);
                }}
              >
                <option value="">Select invoice</option>
                {savedInvoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber || "(No number)"} — {inv.clientName || inv.invoiceFor || "Unknown client"}
                  </option>
                ))}
              </select>
            </div>

            {loadedInvoice && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Loaded invoice total: <b>{formatMoneyGBP(loadedInvoice.total)}</b>
              </div>
            )}
          </div>
        </div>

        {(!loadedInvoice && invoiceRows.length === 0) ? (
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            Load completed shifts or add expense items to generate line items, or select a saved invoice above.
          </div>
        ) : (
          <div id="print-invoice-only">
            <InvoicePreview
              meta={loadedInvoice ? loadedInvoice : {
                invoiceFor,
                payableTo,
                invoiceNumber,
                submittedOn,
                dueDate,
                serviceType,
                notes: [bankLine1, bankLine2, bankLine3].filter(Boolean),
                vatApplicable,
                vatPercent,
              }}
              rows={loadedInvoice ? (loadedInvoice.lineItems || []).map((li) => ({
                shiftId: li.shiftId || li.id || Math.random().toString(36).slice(2),
                dateLabel: li.isCustom ? li.description : ((li.description || "").split(" - ")[0] || "—"),
                timeLabel: li.isCustom ? "" : (li.description?.includes(" - ") ? `(${li.description.split(" - ")[1]})` : ""),
                qtyHoursDisplay: Number(li.hours || 0).toFixed(2),
                minutes: li.minutes || 0,
                rate: li.rate || 0,
                total: li.total || 0,
                isCustom: !!li.isCustom,
              })) : invoiceRows}
              subtotal={loadedInvoice ? (loadedInvoice.subtotal || 0) : subtotal}
              vatAmount={loadedInvoice ? (loadedInvoice.vatAmount || 0) : vatAmount}
              total={loadedInvoice ? (loadedInvoice.total || 0) : grandTotal}
            />
          </div>
        )}

        <div className="no-print" style={{ marginTop: 16, display: "grid", gap: 10 }}>
          <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
            <button
              onClick={saveInvoice}
              disabled={!canSave || saveState.saving}
              style={{
                ...btn("#16a34a"),
                flex: 1,
                opacity: !canSave || saveState.saving ? 0.7 : 1,
                cursor: !canSave || saveState.saving ? "not-allowed" : "pointer",
              }}
            >
              {saveState.saving ? "Saving..." : "Save invoice"}
            </button>

            <button
              onClick={() => window.print()}
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: "10px",
                backgroundColor: "#111827",
                color: "#fff",
                fontWeight: "600",
                border: "none",
                cursor: "pointer",
              }}
            >
              Print / Save as PDF
            </button>
          </div>

          {saveState.ok && (
            <div style={{ fontSize: 13, color: "#15803d", fontWeight: 600 }}>
              ✅ {saveState.ok}
            </div>
          )}
          {saveState.err && (
            <div style={{ fontSize: 13, color: "#dc2626", fontWeight: 600 }}>
              ❌ {saveState.err}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InvoicePreview({ meta, rows, subtotal, vatAmount, total }) {
  const wrap = {
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    overflow: "hidden",
    background: "#fff",
  };

  const topBar = { height: 8, background: "#ef4444" };
  const section = { padding: 18 };
  const company = { fontSize: 22, fontWeight: 400, color: "#ef4444", margin: "10px 0 6px" };
  const title = { fontSize: 40, fontWeight: 700, color: "#3b82f6", margin: "10px 0 6px" };
  const submitted = { fontSize: 16, fontWeight: 700, color: "#ef4444", marginBottom: 14 };

  const metaGrid = {
    display: "grid",
    gridTemplateColumns: "1.4fr 1.2fr 0.8fr",
    gap: 16,
    borderTop: "1px solid #e5e7eb",
    borderBottom: "1px solid #e5e7eb",
    padding: 16,
    background: "#fff",
  };

  const metaLabel = { fontSize: 12, color: "#6b7280", fontWeight: 800, marginBottom: 6 };
  const metaValue = { fontSize: 14, color: "#111827", fontWeight: 500, lineHeight: 1.35 };

  const table = { width: "100%", borderCollapse: "collapse" };
  const th = {
    textAlign: "left",
    fontSize: 12,
    color: "#1d4ed8",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    padding: "6px 10px",
    fontWeight: 900,
  };
  const td = { padding: "12px 12px", borderBottom: "1px solid #e5e7eb", fontSize: 13 };

  return (
    <div style={wrap}>
      <div style={topBar} />

      <div style={section}>
        <div style={company}>Convalesce Care and Recruitment Limited</div>
        <p style={{ fontSize: 12 }}>73 Johnson Rd, Croydon, CR0 2JS</p>
        <p style={{ fontSize: 12 }}>+44 75 99 121 308</p>
        <p style={{ fontSize: 12 }}>admin@convalesceuk.com</p>

        <div style={title}>Invoice</div>
        <div style={submitted}>Submitted on {meta.submittedOn || "—"}</div>

        <div style={metaGrid}>
          <div>
            <div style={metaLabel}>Invoice for</div>
            <div style={metaValue}>{meta.invoiceFor || "—"}</div>
            <div style={{ marginTop: 10, fontSize: 13, color: "#6b7280", fontWeight: 700 }}>
              Private Support
            </div>
          </div>

          <div>
            <div style={metaLabel}>Payable to</div>
            <div style={metaValue}>{meta.payableTo || "—"}</div>

            <div style={{ marginTop: 14 }}>
              <div style={metaLabel}>Service Type</div>
              <div style={{ ...metaValue, textDecoration: "underline" }}>{meta.serviceType || "—"}</div>
            </div>
          </div>

          <div>
            <div style={metaLabel}>Invoice #</div>
            <div style={metaValue}>{meta.invoiceNumber || "—"}</div>

            <div style={{ marginTop: 14 }}>
              <div style={metaLabel}>Due date</div>
              <div style={metaValue}>{meta.dueDate || "—"}</div>
            </div>
          </div>
        </div>

        <table style={table}>
          <thead>
            <tr>
              <th style={{ ...th, width: "58%" }}>Description</th>
              <th style={{ ...th, width: "14%", textAlign: "right" }}>Hours/Qty</th>
              <th style={{ ...th, width: "14%", textAlign: "right" }}>Rate</th>
              <th style={{ ...th, width: "14%", textAlign: "right" }}>Total price</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r, i) => (
              <tr key={r.shiftId} style={{ background: i % 2 === 0 ? "#f3f4f6" : "#fff" }}>
                <td style={td}>
                  <div style={{ fontWeight: 400, color: "#111827" }}>
                    {r.isCustom ? r.dateLabel : `${r.dateLabel} - ${r.timeLabel}`}
                  </div>
                </td>
                <td style={{ ...td, textAlign: "right", fontWeight: 400 }}>{r.qtyHoursDisplay}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 400 }}>{formatMoneyGBP(r.rate)}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 400 }}>{formatMoneyGBP(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 0.7fr", gap: 16, paddingTop: 14 }}>
          <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.7 }}>
            <div style={{ fontWeight: 900, marginBottom: 6, color: "#374151" }}>Notes:</div>
            {(meta.notes || []).map((n, idx) => (
              <div key={idx}>{n}</div>
            ))}
          </div>

          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: "#111827" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#6b7280" }}>Subtotal</span>
                <span>{formatMoneyGBP(subtotal)}</span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                <span style={{ color: "#6b7280" }}>
                  {meta.vatApplicable ? `VAT ${meta.vatPercent || 0}%` : "VAT % not applicable"}
                </span>
                <span>{formatMoneyGBP(vatAmount)}</span>
              </div>

              <div style={{ marginTop: 10, fontSize: 40, color: "#ec4899", fontWeight: 900 }}>
                {formatMoneyGBP(total)}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}